import { computed, onBeforeUnmount, onMounted } from 'vue';
import { appConfig, isE2eMode } from '../providers/config';
import { getHubspotWindow } from './hubspot.types';

const isEnabled = (raw: string | undefined) => raw === 'true' || raw === '1';

const OPEN_GUARD_MS = 300;
let lastOpenedAt = 0;
let isOpenRequested = false;
let areWidgetHandlersBound = false;

const whenConversationsReady = (callback: () => void) => {
  const hubspotWindow = getHubspotWindow();

  if (hubspotWindow.HubSpotConversations) {
    callback();
    return;
  }

  hubspotWindow.hsConversationsOnReady = hubspotWindow.hsConversationsOnReady ?? [];
  hubspotWindow.hsConversationsOnReady.push(callback);
};

const bindWidgetHandlers = () => {
  const conversations = getHubspotWindow().HubSpotConversations;

  if (areWidgetHandlersBound || !conversations) {
    return;
  }

  areWidgetHandlersBound = true;

  conversations.on('widgetLoaded', () => {
    if (!isOpenRequested) {
      return;
    }

    isOpenRequested = false;
    getHubspotWindow().HubSpotConversations?.widget.open();
  });

  conversations.on('widgetClosed', () => {
    getHubspotWindow().HubSpotConversations?.widget.remove();
  });
};

export const useHubspotChat = () => {
  const isChatAvailable = computed(
    () =>
      !isE2eMode &&
      isEnabled(appConfig.VITE_HUBSPOT_CHAT_ENABLED) &&
      !!appConfig.VITE_HUBSPOT_HUB_ID,
  );

  const openChat = () => {
    if (!isChatAvailable.value) {
      return;
    }

    lastOpenedAt = Date.now();

    whenConversationsReady(() => {
      const conversations = getHubspotWindow().HubSpotConversations;
      if (!conversations) {
        return;
      }

      bindWidgetHandlers();

      if (conversations.widget.status().loaded) {
        conversations.widget.open();
        return;
      }

      isOpenRequested = true;
      conversations.widget.load({ widgetOpen: true });
    });
  };

  const closeChat = () => {
    isOpenRequested = false;
    const conversations = getHubspotWindow().HubSpotConversations;

    if (!conversations) {
      return;
    }

    const { loaded, pending } = conversations.widget.status();

    if (!loaded && !pending) {
      return;
    }

    conversations.widget.remove();
  };

  return { isChatAvailable, openChat, closeChat };
};

export const useHubspotChatAutoClose = () => {
  const { isChatAvailable, closeChat } = useHubspotChat();

  let pendingCloseId: ReturnType<typeof setTimeout> | null = null;

  const handlePageClick = () => {
    if (pendingCloseId !== null) {
      clearTimeout(pendingCloseId);
    }

    pendingCloseId = setTimeout(() => {
      pendingCloseId = null;

      if (Date.now() - lastOpenedAt < OPEN_GUARD_MS) {
        return;
      }

      closeChat();
    });
  };

  onMounted(() => {
    if (!isChatAvailable.value) {
      return;
    }

    document.addEventListener('click', handlePageClick, true);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('click', handlePageClick, true);

    if (pendingCloseId !== null) {
      clearTimeout(pendingCloseId);
    }
  });
};
